import { Accordion, Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCSEntries } from '@hero/services/content-stack-service/hooks';

import * as S from './style';

const AccordionIcon = ({ icon }) => (
  <S.AccordionIcon>
    <S.AccordionIconSource src={icon} width={20} />
  </S.AccordionIcon>
);

export const GoalSelection = ({ setGoal = () => {} }) => {
  const { t } = useTranslation();
  const [selectedLibrarySubcategory, setSelectedLibrarySubcategory] = useState(null);
  const [selectedLibraryMessage, setSelectedLibraryMessage] = useState(null);

  const { data: dataLibraryCategory } = useGetCSEntries({
    entryCode: 'sgb_admin_library_category',
    params: { query: { visibility: true } }
  });
  const { data: dataLibrarySubcategory } = useGetCSEntries({
    entryCode: 'sgb_admin_library_subcategory'
  });
  const { data: dataLibraryMessage } = useGetCSEntries({
    entryCode: 'sgb_admin_library_message'
  });

  const library = transformData({
    dataLibraryCategory,
    dataLibrarySubcategory,
    dataLibraryMessage
  });

  const onSelectGoal = value => {
    setSelectedLibrarySubcategory(value);
  };

  useEffect(() => {
    if (selectedLibrarySubcategory && selectedLibraryMessage) {
      setGoal({
        occasion: selectedLibrarySubcategory.id,
        message: selectedLibraryMessage
      });
    }
  }, [selectedLibrarySubcategory, selectedLibraryMessage]);

  return (
    <S.GoalSelection>
      <S.AccordionArea>
        {library?.map(category => {
          return (
            <Accordion
              title={category?.label}
              icon={<AccordionIcon icon={category.icon} />}
              key={category.icon.url}
              variant="outlined"
            >
              <S.AccordionContent>
                {category?.subcategories?.map(subcategory => {
                  return (
                    <S.Library
                      active={selectedLibrarySubcategory?.id === subcategory?.id}
                      onClick={() => {
                        onSelectGoal({
                          id: subcategory?.id,
                          messages: subcategory?.messages
                        });
                        setSelectedLibraryMessage(null);
                      }}
                      key={subcategory?.id}
                    >
                      <S.LibraryName>{subcategory?.label}</S.LibraryName>
                      <S.LibraryCount>{subcategory?.messages?.length || 0}</S.LibraryCount>
                    </S.Library>
                  );
                })}
              </S.AccordionContent>
            </Accordion>
          );
        })}
      </S.AccordionArea>
      <S.MessageArea>
        {selectedLibrarySubcategory?.messages?.length ? (
          selectedLibrarySubcategory?.messages?.map(message => (
            <S.MessageLine
              key={message}
              onClick={() => setSelectedLibraryMessage(message)}
              active={message === selectedLibraryMessage}
            >
              {message}
              {message === selectedLibraryMessage && (
                <Icon name="success" color="var(--color-success-strong)" />
              )}
            </S.MessageLine>
          ))
        ) : (
          <S.LibraryPlaceholder>
            <Typography variant="body2Italic">{`(${t('selectCategoryToStart')})`}</Typography>
          </S.LibraryPlaceholder>
        )}
      </S.MessageArea>
    </S.GoalSelection>
  );
};

function transformData(data) {
  if (!data.dataLibraryCategory || !data.dataLibrarySubcategory || !data.dataLibraryMessage) {
    return [];
  }

  const libraryCategories = data.dataLibraryCategory.entries;
  const librarySubcategories = data.dataLibrarySubcategory.entries;
  const libraryMessages = data.dataLibraryMessage.entries;

  const result = {};

  libraryCategories.forEach(category => {
    result[category.uid] = {
      id: category.uid,
      label: category.label,
      icon: category.icon.url,
      order: category.order || 0,
      subcategories: []
    };
  });

  librarySubcategories.forEach(subcategory => {
    const parentCategoryUid = subcategory.library_category[0].uid;

    if (result[parentCategoryUid]) {
      result[parentCategoryUid].subcategories.push({
        id: subcategory.uid,
        label: subcategory.label,
        messages: []
      });
    }
  });

  libraryMessages.forEach(message => {
    const categoryUid = message.library_category[0].uid;
    const subcategoryUid = message.library_subcategory[0].uid;

    const category = result[categoryUid];
    if (category) {
      const subcategory = category.subcategories.find(sub => sub.id === subcategoryUid);
      if (subcategory) {
        subcategory.messages.push(message.message);
      }
    }
  });

  return Object.values(result).sort((a, b) => (a.order || 0) - (b.order || 0));
}
